import { data } from './data.js';
import { normalizePlace } from './normalize.js';
export { normalizePlace, slugify } from './normalize.js';
/** Gazetteer del Salento (data/comuni.json, generato da sse-ingestion/migrations/build_gazetteer.py). */
export const gazetteer = data;
const PROVINCES = new Set(['le', 'br', 'ta']);
/**
 * Chiave "tollerante" sui santi: san/santa/santo/sant → "s". Serve solo come ripiego quando
 * l'euristica di espansione di "S." sbaglia il genere (es. "S. Foca" → "santa foca", ma la
 * frazione è San Foca).
 */
function saintKey(normalized) {
    return normalized.replace(/(^| )(san|santa|santo|sant)(?= )/g, '$1s');
}
let index;
function getIndex() {
    if (index)
        return index;
    const idx = {
        bySlug: new Map(),
        byIstat: new Map(),
        names: new Map(),
        aliases: new Map(),
        frazioni: new Map(),
        loose: new Map(),
    };
    const addLoose = (key, m) => {
        const k = saintKey(key);
        const list = idx.loose.get(k) ?? [];
        list.push(m);
        idx.loose.set(k, list);
    };
    for (const comune of gazetteer.comuni) {
        idx.bySlug.set(comune.slug, comune);
        idx.byIstat.set(comune.istat, comune);
        const nameKey = normalizePlace(comune.name);
        idx.names.set(nameKey, comune);
        addLoose(nameKey, { comune, match: 'name' });
        for (const alias of comune.aliases) {
            const k = normalizePlace(alias);
            if (!k || k === nameKey)
                continue;
            idx.aliases.set(k, comune);
            addLoose(k, { comune, match: 'alias' });
        }
        for (const frazione of comune.frazioni) {
            const k = normalizePlace(frazione.name);
            const list = idx.frazioni.get(k) ?? [];
            list.push({ comune, frazione });
            idx.frazioni.set(k, list);
            addLoose(k, { comune, frazione, match: 'frazione' });
        }
    }
    index = idx;
    return idx;
}
function lookup(key) {
    if (!key)
        return [];
    const idx = getIndex();
    const byName = idx.names.get(key);
    if (byName)
        return [{ comune: byName, match: 'name' }];
    const byAlias = idx.aliases.get(key);
    if (byAlias)
        return [{ comune: byAlias, match: 'alias' }];
    const fr = idx.frazioni.get(key);
    if (fr?.length)
        return fr.map((f) => ({ ...f, match: 'frazione' }));
    const loose = idx.loose.get(saintKey(key)) ?? [];
    // dedup per comune+frazione
    const seen = new Set();
    return loose.filter((m) => {
        const k = `${m.comune.istat}/${m.frazione?.slug ?? ''}`;
        if (seen.has(k))
            return false;
        seen.add(k);
        return true;
    });
}
/**
 * Tutti i luoghi che corrispondono esattamente al testo (dopo `normalizePlace`), in ordine:
 * nome del comune, alias, frazione (una frazione può esistere in più comuni). Il formato
 * "Frazione (Comune)" e "Comune (LE)" è gestito.
 */
export function findPlaces(text) {
    const paren = /^(.*?)\(([^()]*)\)\s*$/.exec(text);
    if (paren) {
        const outer = normalizePlace(paren[1] ?? '');
        const inner = normalizePlace(paren[2] ?? '');
        if (PROVINCES.has(inner)) {
            const province = inner.toUpperCase();
            return lookup(outer).filter((m) => m.comune.province === province);
        }
        const comuni = lookup(inner).filter((m) => m.match !== 'frazione');
        if (comuni.length === 1) {
            const comune = comuni[0].comune;
            const inComune = lookup(outer).filter((m) => m.comune.istat === comune.istat);
            if (inComune.length)
                return inComune;
            // luogo non noto dentro un comune noto: vale il comune
            return [{ comune, match: comuni[0].match }];
        }
    }
    return lookup(normalizePlace(text));
}
/**
 * Il luogo corrispondente al testo, se è unico (nome, alias o frazione, confronto esatto
 * dopo `normalizePlace`). Restituisce `undefined` se non trovato o ambiguo (es. una frazione
 * con lo stesso nome in due comuni: in quel caso usare `findPlaces`).
 */
export function findComune(text) {
    const all = findPlaces(text);
    if (all.length === 1)
        return all[0];
    // più frazioni omonime nello stesso comune contano come una
    if (all.length > 1 && all.every((m) => m.comune.istat === all[0].comune.istat))
        return all[0];
    return undefined;
}
export function comuneBySlug(slug) {
    return getIndex().bySlug.get(slug);
}
export function comuneByIstat(istat) {
    return getIndex().byIstat.get(istat);
}
/** Nome del comune con la sigla della provincia, per esempio "Martano (LE)". */
export const withProvince = (name, province) => name && province ? `${name} (${province})` : name;
/** "Comune (PROV)" dal codice ISTAT; stringa vuota se il comune non c'è. */
export function comuneLabel(istat) {
    const c = istat ? comuneByIstat(istat) : undefined;
    return c ? withProvince(c.name, c.province) : '';
}
//# sourceMappingURL=index.js.map